import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import logo from '../../assets/logo.jpeg';

export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const { itemCount, setCartOpen } = useCart();
  const navigate    = useNavigate();
  const location    = useLocation();
  const [drawerOpen,  setDrawerOpen]  = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [searchOpen,  setSearchOpen]  = useState(false);
  const [searchQ,     setSearchQ]     = useState('');
  const drawerRef = useRef();

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  useEffect(() => {
    const h = (e) => { if (drawerRef.current && !drawerRef.current.contains(e.target)) setDrawerOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') { setSearchOpen(false); setMobileOpen(false); } };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQ.trim())}`);
      setSearchOpen(false); setSearchQ('');
    }
  };

  const handleLogout = () => { logout(); setDrawerOpen(false); navigate('/'); };

  const navLinks = [
    { to: '/',               label: 'Home'          },
    { to: '/products',       label: 'Products'      },
    { to: '/how-to-measure', label: 'Knowledge Zone'},
    { to: '/price-list',     label: 'Price List'    },
    { to: '/current-stock',  label: 'Current Stock' },
    { to: '/about',          label: 'About'         },
    { to: '/feedback',       label: 'Feedback/Inquiry'      },
  ];

  return (
    <>
      <nav className="fixed top-0 w-full z-40 bg-white/90 backdrop-blur-md border-b border-outline-variant/30">
        <div className="flex items-center justify-between px-6 md:px-10 h-20 max-w-screen-2xl mx-auto">

          {/* Logo */}
<Link to="/" className="flex items-center gap-2 group shrink-0">
  <img src={logo} alt="Central Fasteners Logo" className="h-8 w-8 sm:h-9 sm:w-9 rounded-md object-cover" />
  <div className="flex flex-col leading-none">
    <span className="font-Lato font-black text-sm sm:text-xl tracking-tighter text-on-surface group-hover:text-primary-container transition-colors whitespace-nowrap">
      Central Fasteners
    </span>
    <span className="hidden sm:block font-label text-[10px] uppercase tracking-[0.22em] text-secondary">
      Strength. Precision. Relability
    </span>
  </div>
</Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-7">
            {navLinks.map(({ to, label }) => (
              <Link key={to} to={to}
                className={`font-headline uppercase tracking-tight text-sm font-bold transition-colors whitespace-nowrap ${
                  isActive(to) ? 'text-primary-container border-b-2 border-primary-container pb-0.5' : 'text-secondary hover:text-on-surface'
                }`}>{label}</Link>
            ))}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            <button onClick={() => setSearchOpen(true)} className="p-2 text-secondary hover:text-primary-container transition-colors" aria-label="Search">
              <span className="material-symbols-outlined text-[22px]">search</span>
            </button>

            {/* Inquiry list */}
            <button
              onClick={() => user ? setCartOpen(true) : navigate('/login')}
              className="relative p-2 text-secondary hover:text-primary-container transition-colors"
              aria-label="Inquiry List">
              <span className="material-symbols-outlined text-[22px]">list_alt</span>
              {itemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-primary-container text-white text-[10px] font-black w-5 h-5 flex items-center justify-center leading-none">
                  {itemCount > 99 ? '99+' : itemCount}
                </span>
              )}
            </button>

            {/* User dropdown */}
            <div className="relative" ref={drawerRef}>
              <button onClick={() => setDrawerOpen(!drawerOpen)} className="p-2 text-secondary hover:text-primary-container transition-colors" aria-label="Account">
                <span className="material-symbols-outlined text-[22px]">{user ? 'account_circle' : 'person'}</span>
              </button>

              {drawerOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white shadow-xl border border-outline-variant/30 z-50">
                  {user ? (
                    <>
                      <div className="px-4 py-3 border-b border-surface-container">
                        <p className="font-label text-xs uppercase tracking-widest text-secondary">Signed in as</p>
                        <p className="font-headline font-bold text-sm text-on-surface truncate">{user.name}</p>
                      </div>
                      <Link to="/profile" onClick={() => setDrawerOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-on-surface hover:bg-surface-container-low transition-colors">
                        <span className="material-symbols-outlined text-[18px] text-secondary">manage_accounts</span> My Profile
                      </Link>
                      {isAdmin && (
                        <Link to="/admin" onClick={() => setDrawerOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-primary-container font-bold hover:bg-surface-container-low transition-colors">
                          <span className="material-symbols-outlined text-[18px]">admin_panel_settings</span> Admin Panel
                        </Link>
                      )}
                      <div className="border-t border-surface-container">
                        <button onClick={handleLogout}
                          className="flex items-center gap-3 w-full px-4 py-3 text-sm text-error hover:bg-red-50 transition-colors">
                          <span className="material-symbols-outlined text-[18px]">logout</span> Logout
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <Link to="/login" onClick={() => setDrawerOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-on-surface hover:bg-surface-container-low font-bold">
                        <span className="material-symbols-outlined text-[18px] text-secondary">login</span> Sign In
                      </Link>
                      <Link to="/register" onClick={() => setDrawerOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-on-surface hover:bg-surface-container-low">
                        <span className="material-symbols-outlined text-[18px] text-secondary">person_add</span> Register
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Mobile menu toggle */}
            <button className="lg:hidden p-2 text-secondary" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
              <span className="material-symbols-outlined">{mobileOpen ? 'close' : 'menu'}</span>
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-outline-variant/30">
            {navLinks.map(({ to, label }) => (
              <Link key={to} to={to} onClick={() => setMobileOpen(false)}
                className={`block px-6 py-4 font-headline font-bold uppercase tracking-tight text-sm border-b border-surface-container transition-colors ${
                  isActive(to) ? 'text-primary-container bg-surface-container-low' : 'text-on-surface hover:bg-surface-container-low'
                }`}>{label}</Link>
            ))}
          </div>
        )}
      </nav>

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-on-surface/60 backdrop-blur-sm flex items-start justify-center pt-32 px-4">
          <form onSubmit={handleSearch} className="w-full max-w-2xl">
            <div className="flex items-center bg-white border-b-4 border-primary-container px-6 py-4 gap-4">
              <span className="material-symbols-outlined text-primary-container text-[28px]">search</span>
              <input autoFocus value={searchQ} onChange={e => setSearchQ(e.target.value)}
                className="flex-1 bg-transparent font-headline text-xl font-bold text-on-surface placeholder-outline-variant focus:outline-none uppercase tracking-tight"
                placeholder="SEARCH PRODUCTS, SKU..." />
              <button type="button" onClick={() => setSearchOpen(false)} className="text-secondary hover:text-error">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <p className="mt-3 text-center font-label text-xs text-white/70 uppercase tracking-widest">Press Enter to search · Esc to close</p>
          </form>
        </div>
      )}
    </>
  );
}
